import { useEffect } from "react";

/**
 * Custom hook that listens for a specific key press and submits the closest form when that key is pressed.
 *
 * If you find yourself wandering to this page, debugging odd behavior of a form submission on key press,
 * check if this hook is being used in the component tree and if the key matches the one you're pressing.
 *
 * If you want to avoid this behavior for a specific form or button, you can add the
 * class "no-autobind-{key}" to that element (e.g. "no-autobind-F10" to avoid Enter key submission).
 *
 * @param {*} key
 */
export function useSubmitFormOnKey(key = "F10") {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === key) {
        const form =
          document.activeElement?.closest("form") ||
          document.querySelector("form");

        if (form) {
          e.preventDefault();

          const btn = findSubmitButton(form);

          if (btn?.disabled) {
            console.debug(
              `Found submit button in form to submit by pressing ${key} key, but it's disabled. Skipping.`,
              btn
            );
            return;
          }

          form.requestSubmit();
          console.debug(`Submitting form by pressing ${key} key`, form);
          return;
        }

        const not = `:not(.icon):not(.no-autobind-${key})`;

        clickOnMatch(
          e,

          // be very explicit about what we want to click on,
          // to avoid unintended consequences of binding global
          // keys to generic elements like buttons or inputs
          `.autobind-${key}`,

          // is there any submit button we can click on?
          // if so, click the last one (in case there are multiple forms on the page)
          `button[type="submit"]${not}`,

          // or any input with type=submit
          `input[type="submit"]${not}`,

          // less and less picky... any button will do...
          `button${not}`
        );
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [key]);
}

/**
 * Iterates through all selectors, find all matching elements for each and clicks on the last matched element
 *
 * @param  {...string} query
 */
function clickOnMatch(e, ...query) {
  query.forEach((q) => {
    const el = document.querySelectorAll(q);

    if (el.length > 0) {
      // prevent default ONLY if we have a match

      e.preventDefault();

      // intentionlly clicking the last matched element,
      // in case there are multiple matches on the page
      // (e.g. multiple forms with submit buttons)
      const btn = el[el.length - 1];

      if (btn.disabled) {
        console.debug(
          `Found ${q} element to click on, but it's disabled. Skipping.`,
          btn
        );
        return;
      }

      console.debug(`Submitting form by clicking on ${q} element`, btn);

      btn.click();
    }
  });
}

function findSubmitButton(form) {
  return (
    form.querySelector('button[type="submit"]') ||
    form.querySelector('input[type="submit"]') ||
    form.querySelector("button")
  );
}
